import { Settings, Search, LogOut, Briefcase, Loader2, Folder } from "lucide-react";
import CountryFlag from "@/components/ui/CountryFlag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage, languages, LanguageCode } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchQuery } from "@/hooks/queries/useSearchQuery";
import { useCurrentUserProfileQuery, useUpdateUserSettingsMutation } from "@/hooks/queries/useUserSettingsQuery";
import { useState, useRef, useCallback } from "react";
import type { SearchResultItem } from "@/types/search";

const AppHeader = () => {
  const navigate = useNavigate();
  const { language, setLanguage, currentLanguage } = useLanguage();
  const { t } = useTranslation(language);
  const { logout, user } = useAuth();
  const { data: profile } = useCurrentUserProfileQuery();
  const updateSettings = useUpdateUserSettingsMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: searchResults, isFetching } = useSearchQuery(searchTerm);

  const hasResults =
    searchResults &&
    (searchResults.projects.length > 0 || searchResults.folders.length > 0);

  // Derive display name and initials from the user profile
  const displayName = user?.username || user?.name || user?.email || "";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPopoverOpen(e.target.value.trim().length >= 2);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setSearchTerm("");
      setPopoverOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleResultClick = useCallback((item: SearchResultItem) => {
    setSearchTerm("");
    setPopoverOpen(false);
    if (item.entityType === "Project") {
      navigate(`/project/${item.id}`);
    } else if (item.entityType === "Folder" && item.parentId != null) {
      navigate(`/project/${item.parentId}?tab=folders&folderId=${item.id}`);
    }
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Sign out error:", error);
      navigate("/");
    }
  };

  const handleLanguageChange = (code: LanguageCode) => {
    setLanguage(code);
    if (profile) {
      updateSettings.mutate(
        { userId: profile.id, settings: { language: code } },
        { onError: (err) => console.error("Failed to persist language preference:", err) },
      );
    }
  };

  return (
    <header className="sticky top-0 z-50 h-16 bg-background border-b border-border flex items-center justify-between px-6">
      {/* Left side - Search */}
      <div className="flex items-center gap-4">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              {isFetching && searchTerm.length >= 2 && (
                <Loader2
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin"
                  size={14}
                />
              )}
              <Input
                ref={inputRef}
                className="pl-10 w-72 bg-muted/50"
                placeholder={t("search")}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (searchTerm.trim().length >= 2) setPopoverOpen(true);
                }}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-72 p-0 max-h-80 overflow-y-auto"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {searchResults?.projects && searchResults.projects.length > 0 && (
              <div>
                <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                  Projects
                </p>
                {searchResults.projects.map((item) => (
                  <button
                    key={`project-${item.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/60 transition-colors"
                    onClick={() => handleResultClick(item)}
                  >
                    <Briefcase size={14} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      {item.subtitle && item.subtitle !== item.name && (
                        <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchResults?.folders && searchResults.folders.length > 0 && (
              <div>
                <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                  Folders
                </p>
                {searchResults.folders.map((item) => (
                  <button
                    key={`folder-${item.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/60 transition-colors"
                    onClick={() => handleResultClick(item)}
                  >
                    <Folder size={14} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      {item.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!isFetching && searchTerm.trim().length >= 2 && !hasResults && (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                No results found
              </p>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Right side - Actions & User */}
      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <CountryFlag countryCode={currentLanguage.flag} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-background border border-border shadow-lg z-50">
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as LanguageCode)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <CountryFlag countryCode={lang.flag} />
                <span>{lang.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
          <Bell size={20} />
        </Button> */}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/settings")}
          title={t("settings")}
        >
          <Settings size={20} />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground"
          onClick={handleSignOut}
          title={t("signOut")}
        >
          <LogOut size={20} />
        </Button>

        <div className="flex items-center gap-3 ml-2 pl-4 border-l border-border">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium">{displayName}</p>
          </div>
          <Avatar>
            <AvatarImage src="" />
            <AvatarFallback className="bg-violet-600 text-white">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
